import type { LawFirm } from '@common/types/lawfirms';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import LogoImageUpload from '@/components/LogoImageUpload';
import { updateLawFirmLogo } from '@/services/LawfirmService';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';

interface Props {
    selectedLawFirm: LawFirm;
}

export default function Logo({ selectedLawFirm }: Props) {
    const lawFirmId = selectedLawFirm.lawFirmId;
    const queryClient = useQueryClient();
    const [pendingFileId, setPendingFileId] = useState<string | null | undefined>(undefined);
    const logoFileId =
        pendingFileId === null
            ? undefined
            : (pendingFileId ?? selectedLawFirm.logoUrl ?? undefined);

    const updateLogoMutation = useMutation({
        mutationFn: (fileId: string) => updateLawFirmLogo(lawFirmId, fileId),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['lawFirm', lawFirmId] });
            setPendingFileId(undefined);
            successSnackbar('Logo updated successfully');
        },
        onError: () => {
            errorSnackbar('Failed to update logo');
        },
    });

    const deleteLogoMutation = useMutation({
        mutationFn: () => updateLawFirmLogo(lawFirmId, ''),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['lawFirm', lawFirmId] });
            setPendingFileId(undefined);
            successSnackbar('Logo deleted successfully');
        },
        onError: () => {
            errorSnackbar('Failed to delete logo');
        },
    });

    return (
        <Stack spacing={2}>
            <Typography variant="h1">Logo Configuration</Typography>
            <Box sx={{ maxHeight: '200px', maxWidth: '200px' }}>
                <LogoImageUpload
                    lawFirmId={selectedLawFirm.lawFirmId}
                    fileId={logoFileId}
                    validations={{
                        dimensions: {
                            min: { height: 200, width: 200 },
                            max: { height: 400, width: 400 },
                            aspectRatio: 1,
                        },
                        size: 2 * 1024 * 1024,
                        acceptedTypes: ['image/jpeg', 'image/jpg', 'image/png'],
                    }}
                    onUploadSuccess={(fileId) => setPendingFileId(fileId)}
                    onRemoveFile={() => {
                        setPendingFileId(null);
                        deleteLogoMutation.mutate();
                    }}
                />
            </Box>
            <Button
                variant="contained"
                disabled={!pendingFileId || updateLogoMutation.isPending}
                onClick={() => pendingFileId && updateLogoMutation.mutate(pendingFileId)}
                sx={{ width: 'fit-content' }}
            >
                {updateLogoMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
        </Stack>
    );
}
